/**
 * TenantStatsCard — "Indicadores do período" agregado de todos os ativos
 * de UM tenant. Vive no Dashboard.
 *
 * Range picker próprio (24h / 7d / 30d / personalizado).
 * Esconde-se quando super_admin não escolheu tenant ainda (parent decide
 * passando tenantId=undefined).
 *
 * Big numbers:
 *   1) Temperatura média global + SLI/SLO (ponderado pelo tempo)
 *   2) Energia: kWh total + R$ estimado
 *   3) Aberturas: contagem + tempo total
 *   4) Uptime % (média ponderada por equipment)
 */

import { useMemo, useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  DoorOpen,
  Thermometer,
  WifiHigh,
  Zap,
} from "lucide-react";
import clsx from "clsx";

import { useTenantStats } from "@/hooks/queries";
import { formatDuration } from "@/lib/format";

import { Card, CardBody, CardHeader } from "./Card";
import { EnergiaAgora } from "./EnergiaAgora";
import { Field, Input, Select } from "./Field";

// ============================================================================
// Range picker — mesma UX do EquipmentDetail
// ============================================================================

// A série de energia troca de grandeza conforme o período (ver
// `power_series_unit`): período curto responde "quanto está puxando", com a
// curva de potência; período longo responde "quanto consumiu por dia", com
// barras de kWh.
//
// NENHUMA opção daqui pode coincidir com o corte no servidor. A tela manda só
// o `from` e deixa o `to` em aberto, então o span que chega lá é sempre o
// nominal MAIS a latência da requisição — e uma opção em cima do corte cai do
// lado errado por causa disso. O corte hoje é 4 dias, longe de todas.
const RANGE_OPTIONS = [
  { value: "12h",    label: "Últimas 12h",     fromHours: 12 },
  { value: "24h",    label: "Últimas 24h",     fromHours: 24 },
  { value: "7d",     label: "Últimos 7 dias",  fromHours: 24 * 7 },
  { value: "30d",    label: "Últimos 30 dias", fromHours: 24 * 30 },
  { value: "90d",    label: "Últimos 90 dias", fromHours: 24 * 90 },
  { value: "custom", label: "Personalizado",   fromHours: 0 },
];

function isoToLocalDatetime(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Aberto/fechado é preferência de PESSOA, não de tenant: a pergunta é quanto
 * espaço ela quer dar pro dashboard, e isso não muda de cliente pra cliente.
 * Mesma chave e mesmo comportamento do card de insight.
 *
 * Fechado por padrão. Os quatro indicadores continuam visíveis na faixa
 * fechada — o colapso tira os GRÁFICOS, não o dado —, então nada fica
 * escondido atrás de um clique: quem quiser a leitura rápida já tem, e quem
 * for investigar abre.
 */
const CHAVE_ABERTO = "envpulse:indicadores-abertos";

function lerPreferencia(): boolean | null {
  try {
    const v = localStorage.getItem(CHAVE_ABERTO);
    return v === null ? null : v === "1";
  } catch {
    return null;   // navegador com storage bloqueado — cai no default
  }
}

function gravarPreferencia(aberto: boolean) {
  try {
    localStorage.setItem(CHAVE_ABERTO, aberto ? "1" : "0");
  } catch {
    // sem storage: a escolha vale só nesta sessão, e tudo bem
  }
}

interface Props {
  /** Tenant alvo. Se undefined, o card NÃO renderiza (super_admin sem filtro). */
  tenantId: string | undefined;
  /** Restringe a UMA unidade. É o que transforma este card no painel do site
   *  sem duplicar consulta, agregação nem layout — a pergunta é a mesma, só
   *  o recorte muda. */
  siteId?: string;
  /** Título alternativo. Na tela da unidade, "Indicadores do período" sem
   *  dizer de quê fica ambíguo ao lado do nome do site. */
  titulo?: string;
}

export function TenantStatsCard({ tenantId, siteId, titulo }: Props) {
  const [aberto, setAberto] = useState(() => lerPreferencia() ?? false);
  function alternar() {
    setAberto((v) => {
      gravarPreferencia(!v);
      return !v;
    });
  }

  const [rangeKey, setRangeKey] = useState("7d");
  const range = RANGE_OPTIONS.find((r) => r.value === rangeKey)!;

  const [customDraft, setCustomDraft] = useState(() => {
    const now = Date.now();
    return {
      from: isoToLocalDatetime(new Date(now - 7 * 24 * 3600 * 1000).toISOString()),
      to:   isoToLocalDatetime(new Date(now).toISOString()),
    };
  });
  const [customApplied, setCustomApplied] = useState<{ from: string; to: string } | null>(null);

  const { fromIso, toIso } = useMemo(() => {
    if (rangeKey === "custom" && customApplied) {
      return {
        fromIso: new Date(customApplied.from).toISOString(),
        toIso:   new Date(customApplied.to).toISOString(),
      };
    }
    return {
      fromIso: new Date(Date.now() - range.fromHours * 3600 * 1000).toISOString(),
      toIso:   undefined as string | undefined,
    };
  }, [rangeKey, range, customApplied]);

  function applyCustomRange() {
    if (!customDraft.from || !customDraft.to) return;
    if (new Date(customDraft.from) >= new Date(customDraft.to)) {
      // eslint-disable-next-line no-alert
      alert("A data inicial precisa ser anterior à final.");
      return;
    }
    setCustomApplied({ from: customDraft.from, to: customDraft.to });
  }

  const stats = useTenantStats(tenantId, { from: fromIso, to: toIso, site_id: siteId });

  // Super_admin sem tenant filtrado → não renderiza nada.
  if (!tenantId) return null;

  const loading = stats.isLoading || !stats.data;
  const data = stats.data;

  return (
    <Card>
      <CardHeader
        title={titulo ?? "Indicadores do período"}
        subtitle={
          data
            ? `${data.equipments_count} ativo${data.equipments_count === 1 ? "" : "s"} agregado${data.equipments_count === 1 ? "" : "s"}`
            : "Carregando…"
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Field className="w-44">
              <Select
                value={rangeKey}
                onChange={(e) => setRangeKey(e.target.value)}
                options={RANGE_OPTIONS.map((r) => ({ value: r.value, label: r.label }))}
              />
            </Field>
            {rangeKey === "custom" && (
              <div className="flex flex-wrap items-end gap-2">
                <label className="flex flex-col text-[11px] text-slate-500 dark:text-slate-400">
                  De
                  <input
                    type="datetime-local"
                    value={customDraft.from}
                    onChange={(e) => setCustomDraft((d) => ({ ...d, from: e.target.value }))}
                    className="rounded-md border border-surface-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400/40"
                  />
                </label>
                <label className="flex flex-col text-[11px] text-slate-500 dark:text-slate-400">
                  Até
                  <input
                    type="datetime-local"
                    value={customDraft.to}
                    onChange={(e) => setCustomDraft((d) => ({ ...d, to: e.target.value }))}
                    className="rounded-md border border-surface-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400/40"
                  />
                </label>
                <button
                  type="button"
                  onClick={applyCustomRange}
                  className="rounded-md border border-surface-300 dark:border-slate-700 px-3 py-1 text-sm font-medium hover:bg-surface-50 dark:hover:bg-slate-800"
                >
                  Aplicar
                </button>
              </div>
            )}
            <button
              type="button"
              onClick={alternar}
              aria-expanded={aberto}
              className="inline-flex items-center gap-1 rounded-md border border-surface-300 px-2.5 py-1 text-sm font-medium text-slate-600 hover:bg-surface-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              <ChevronDown
                className={clsx("h-4 w-4 transition-transform", aberto && "rotate-180")}
                aria-hidden
              />
              {aberto ? "Recolher" : "Gráficos"}
            </button>
          </div>
        }
      />
      <CardBody>
        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-28 rounded-lg bg-surface-100 dark:bg-slate-800/40 animate-pulse"
              />
            ))}
          </div>
        ) : !aberto ? (
          /* Faixa fechada: só os quatro indicadores, lado a lado, como era
             antes dos gráficos existirem. É a leitura de relance — quatro
             números e nada pra interpretar. Os gráficos ficam a um clique,
             porque eles respondem a outra pergunta ("por que esse número") e
             essa não se faz o tempo todo. */
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <TemperatureBigNumber data={data!.temperature} />
            <EnergyBigNumber data={data!.energy} />
            <DoorBigNumber data={data!.door} />
            <UptimeBigNumber data={data!.uptime} />
          </div>
        ) : (
          <>
            {/* Duas colunas a partir de lg: os quatro indicadores empilhados
                à esquerda, os dois gráficos à direita. As linhas do grid são
                as MESMAS nas duas colunas, então o gráfico de temperatura
                fica na altura do indicador de temperatura e o de energia
                ocupa exatamente as três linhas restantes — o alinhamento sai
                da grade, não de altura fixa, e continua valendo quando um
                indicador cresce.

                Abaixo de lg vira uma coluna só, na ordem em que se lê:
                indicador, e o gráfico dele logo em seguida. */}
            {/* Quem dita a altura das linhas são os INDICADORES, nunca os
                gráficos.

                Duas coisas fazem isso funcionar, e sem as duas o layout
                desmonta como desmontou na primeira tentativa:

                 • As linhas são AUTO. Foi a segunda tentativa que errou
                   aqui: com `grid-rows-4`, as quatro linhas viram `1fr`,
                   ficam todas do tamanho da MAIOR, e cada tile passa a boiar
                   dentro de uma linha alta demais — exatamente o
                   "espaçamento incoerente" que apareceu na tela. Em `auto`,
                   cada linha tem a altura do seu tile e o respiro entre eles
                   é só o `gap`.

                 • O conteúdo da coluna direita é posicionado em ABSOLUTO
                   dentro da célula. Elemento absoluto não conta pra altura da
                   linha — foi o gráfico de energia, mais alto que três
                   indicadores, que empurrou tudo pra baixo e desalinhou. Aqui
                   ele recebe a altura que sobra e se ajusta a ela.

                Abaixo de lg vira uma coluna só, e aí o posicionamento volta
                ao normal (`lg:absolute` e `lg:static`). */}
            <div className="grid gap-3 lg:grid-cols-[minmax(190px,230px)_1fr]">
              <div className="lg:col-start-1 lg:row-start-1">
                <TemperatureBigNumber data={data!.temperature} />
              </div>
              <div className="relative min-h-0 lg:col-start-2 lg:row-start-1">
                <div className="lg:absolute lg:inset-0">
                  <TempSparkline series={data!.temp_series} />
                </div>
              </div>
              <div className="lg:col-start-1 lg:row-start-2">
                <EnergyBigNumber data={data!.energy} />
              </div>
              <div className="lg:col-start-1 lg:row-start-3">
                <DoorBigNumber data={data!.door} />
              </div>
              <div className="lg:col-start-1 lg:row-start-4">
                <UptimeBigNumber data={data!.uptime} />
              </div>
              <div className="relative min-h-0 lg:col-start-2 lg:row-start-2 lg:row-span-3">
                <div className="lg:absolute lg:inset-0">
                  <EnergiaAgora data={data!} />
                </div>
              </div>
            </div>
          </>
        )}
      </CardBody>
    </Card>
  );
}

// ============================================================================
// Sub-componentes — copiados/adaptados do EquipmentStatsCard
// ============================================================================

function TemperatureBigNumber({
  data,
}: {
  data: NonNullable<ReturnType<typeof useTenantStats>["data"]>["temperature"];
}) {
  const avgTxt = data.avg !== null ? `${data.avg.toFixed(1)} °C` : "—";
  const slo = data.slo_target_pct;
  const sli = data.sli_pct;
  const ringColor =
    sli === null
      ? "text-slate-300 dark:text-slate-700"
      : sli >= slo
      ? "text-emerald-500"
      : sli >= 95
      ? "text-amber-500"
      : "text-red-500";

  return (
    <div className="rounded-lg border border-surface-200 dark:border-slate-700 p-3">
      <div className="flex items-center justify-between mb-1">
        <span className="inline-flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
          <Thermometer className="h-3.5 w-3.5" />
          Temperatura
        </span>
        {sli !== null && <SloRing pct={sli} colorClass={ringColor} />}
      </div>
      <div className="text-2xl font-bold tabular-nums text-slate-900 dark:text-slate-100">
        {avgTxt}
      </div>
      <div className="mt-1 flex items-center gap-3 text-[11px] text-slate-500 dark:text-slate-400 tabular-nums">
        {data.min !== null && (
          <span className="inline-flex items-center gap-0.5">
            <ArrowDown className="h-3 w-3" />
            {data.min.toFixed(1)}°
          </span>
        )}
        {data.max !== null && (
          <span className="inline-flex items-center gap-0.5">
            <ArrowUp className="h-3 w-3" />
            {data.max.toFixed(1)}°
          </span>
        )}
        {sli !== null && (
          <span className="ml-auto">
            SLI {sli.toFixed(1)}% / SLO {slo.toFixed(0)}%
          </span>
        )}
      </div>
    </div>
  );
}

function SloRing({ pct, colorClass }: { pct: number; colorClass: string }) {
  const r = 14;
  const c = 2 * Math.PI * r;
  const offset = c - (Math.max(0, Math.min(100, pct)) / 100) * c;
  return (
    <svg width="36" height="36" viewBox="0 0 36 36" className="flex-none">
      <circle
        cx="18"
        cy="18"
        r={r}
        fill="none"
        strokeWidth="3.5"
        className="text-surface-200 dark:text-slate-700"
        stroke="currentColor"
      />
      <circle
        cx="18"
        cy="18"
        r={r}
        fill="none"
        strokeWidth="3.5"
        strokeLinecap="round"
        className={colorClass}
        stroke="currentColor"
        strokeDasharray={c}
        strokeDashoffset={offset}
        transform="rotate(-90 18 18)"
        style={{ transition: "stroke-dashoffset 0.4s ease" }}
      />
    </svg>
  );
}

function EnergyBigNumber({
  data,
}: {
  data: NonNullable<ReturnType<typeof useTenantStats>["data"]>["energy"];
}) {
  const kwhTxt = data.total_kwh < 10 ? data.total_kwh.toFixed(2) : data.total_kwh.toFixed(1);
  const costTxt = new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(data.estimated_cost);
  return (
    <div className="rounded-lg border border-surface-200 dark:border-slate-700 p-3">
      <span className="inline-flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
        <Zap className="h-3.5 w-3.5 text-amber-500" />
        Energia
      </span>
      <div className="mt-1 text-2xl font-bold tabular-nums text-slate-900 dark:text-slate-100">
        {kwhTxt}{" "}
        <span className="text-base font-medium text-slate-500 dark:text-slate-400">kWh</span>
      </div>
      <div className="mt-1 flex items-center justify-between text-[11px] text-slate-500 dark:text-slate-400 tabular-nums">
        <span>{costTxt}</span>
        <span>
          {data.avg_power_w !== null ? `~${Math.round(data.avg_power_w)} W médio` : "—"}
        </span>
      </div>
      <div className="mt-0.5 text-[10px] text-slate-400 dark:text-slate-500">
        tarifa R$ {data.tariff_per_kwh.toFixed(2)}/kWh
      </div>
    </div>
  );
}

function DoorBigNumber({
  data,
}: {
  data: NonNullable<ReturnType<typeof useTenantStats>["data"]>["door"];
}) {
  return (
    <div className="rounded-lg border border-surface-200 dark:border-slate-700 p-3">
      <span className="inline-flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
        <DoorOpen className="h-3.5 w-3.5 text-amber-500" />
        Aberturas
      </span>
      <div className="mt-1 text-2xl font-bold tabular-nums text-slate-900 dark:text-slate-100">
        {data.open_count}
        <span className="text-base font-medium text-slate-500 dark:text-slate-400">×</span>
      </div>
      <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
        total aberta:{" "}
        <span className="tabular-nums text-slate-700 dark:text-slate-300">
          {data.total_open_seconds > 0 ? formatDuration(data.total_open_seconds, true) : "—"}
        </span>
      </div>
    </div>
  );
}

function UptimeBigNumber({
  data,
}: {
  data: NonNullable<ReturnType<typeof useTenantStats>["data"]>["uptime"];
}) {
  const pct = data.online_pct;
  const pctTxt = pct === null ? "—" : `${pct.toFixed(1)}%`;
  const tone =
    pct === null
      ? "text-slate-400"
      : pct >= 99
      ? "text-emerald-600 dark:text-emerald-400"
      : pct >= 95
      ? "text-amber-600 dark:text-amber-400"
      : "text-red-600 dark:text-red-400";
  return (
    <div className="rounded-lg border border-surface-200 dark:border-slate-700 p-3">
      <span className="inline-flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
        <WifiHigh className="h-3.5 w-3.5 text-cyan-500" />
        Uptime
      </span>
      <div className={clsx("mt-1 text-2xl font-bold tabular-nums", tone)}>{pctTxt}</div>
      <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
        {data.samples > 0 ? `${data.samples} leituras no período` : "—"}
      </div>
    </div>
  );
}

/**
 * Formata um Date pra label compacta de eixo X. Adapta a granularidade
 * ao range (se for < 36h mostra hora, senão mostra dia+mês).
 */
function _formatAxisDate(date: Date, spanHours: number): string {
  if (spanHours <= 36) {
    return date.toLocaleTimeString("pt-BR", {
      hour:   "2-digit",
      minute: "2-digit",
    });
  }
  return date.toLocaleDateString("pt-BR", {
    day:   "2-digit",
    month: "short",
  });
}

function TempSparkline({
  series,
}: {
  series: { time: string; value: number | null }[];
}) {
  const stats = useMemo(() => {
    const valid = series
      .map((p) => (p.value !== null ? { time: new Date(p.time), value: p.value } : null))
      .filter((p): p is { time: Date; value: number } => p !== null);
    if (valid.length < 2) return null;

    const values = valid.map((p) => p.value);
    const min  = Math.min(...values);
    const max  = Math.max(...values);
    const avg  = values.reduce((s, v) => s + v, 0) / values.length;
    const last = valid[valid.length - 1];
    const first = valid[0];
    const spanHours = (last.time.getTime() - first.time.getTime()) / (3600 * 1000);

    return { valid, min, max, avg, last, first, spanHours };
  }, [series]);

  if (!stats) return null;
  const { valid, min, max, avg, last, first, spanHours } = stats;

  // ARQUITETURA: SVG só pros elementos VETORIAIS (curva, área, linhas de
  // referência). Textos ficam em <div> sobrepostos via posicionamento
  // ABSOLUTO em PORCENTAGEM — assim acompanham o stretch horizontal do
  // SVG mas mantêm tipografia normal (não distorce).
  //
  // Problema anterior: SVG com preserveAspectRatio="none" estica TUDO,
  // inclusive os <text> internos, deixando fontes deformadas em telas
  // largas. A solução clássica é HTML overlay.
  const w = 600;
  const h = 96;
  const padL = 32;
  const padR = 50;
  const padT = 8;
  const padB = 18;

  const yRange = Math.max(0.5, (max - min) || 0.5);
  const yMargin = yRange * 0.05;
  const yMin = min - yMargin;
  const yMax = max + yMargin;
  const yScale = (v: number) =>
    h - padB - ((v - yMin) / (yMax - yMin)) * (h - padT - padB);

  const t0 = first.time.getTime();
  const tN = last.time.getTime();
  const tSpan = Math.max(1, tN - t0);
  const xScale = (t: number) =>
    padL + ((t - t0) / tSpan) * (w - padL - padR);

  const pathD = valid
    .map((p, i) => `${i === 0 ? "M" : "L"} ${xScale(p.time.getTime()).toFixed(1)},${yScale(p.value).toFixed(1)}`)
    .join(" ");

  const areaD =
    pathD +
    ` L ${xScale(tN).toFixed(1)},${(h - padB).toFixed(1)}` +
    ` L ${xScale(t0).toFixed(1)},${(h - padB).toFixed(1)} Z`;

  const lastX = xScale(last.time.getTime());
  const lastY = yScale(last.value);
  const avgY  = yScale(avg);

  // Converte coords do viewBox pra % do container — usado nos overlays HTML.
  const pctX = (x: number) => `${(x / w) * 100}%`;
  const pctY = (y: number) => `${(y / h) * 100}%`;

  const badgeAbove = lastY > h / 2;

  return (
    <div className="flex h-full flex-col rounded-lg border border-surface-200 p-3 dark:border-slate-700">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Temperatura média no período
        </div>
        <div className="text-[11px] tabular-nums text-slate-500 dark:text-slate-400">
          mín <span className="font-medium text-slate-700 dark:text-slate-300">{min.toFixed(1)}°</span>
          {" · "}
          méd <span className="font-medium text-slate-700 dark:text-slate-300">{avg.toFixed(1)}°</span>
          {" · "}
          máx <span className="font-medium text-slate-700 dark:text-slate-300">{max.toFixed(1)}°</span>
        </div>
      </div>

      {/* Container relativo pra HTML overlay ficar posicionado em cima do SVG.
          A altura é MÍNIMA e não fixa: dentro da grade do dashboard o cartão
          recebe a altura da linha, e altura fixa é o que fazia ele estourar
          o indicador ao lado. */}
      <div className="relative min-h-0 flex-1" style={{ minHeight: 64 }}>
        <svg
          viewBox={`0 0 ${w} ${h}`}
          preserveAspectRatio="none"
          style={{ width: "100%", height: "100%" }}
          role="img"
          aria-label={`Sparkline de temperatura. Mínimo ${min.toFixed(1)}°C, média ${avg.toFixed(1)}°C, máximo ${max.toFixed(1)}°C.`}
        >
          <defs>
            <linearGradient id="tempspark-fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor="currentColor" stopOpacity="0.25" />
              <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
            </linearGradient>
          </defs>

          {/* Linhas de referência (min, max) tracejadas */}
          <g className="text-slate-300 dark:text-slate-700">
            <line x1={padL} x2={w - padR} y1={yScale(min)} y2={yScale(min)}
                  stroke="currentColor" strokeWidth="0.5" strokeDasharray="2,3"
                  vectorEffect="non-scaling-stroke" />
            <line x1={padL} x2={w - padR} y1={yScale(max)} y2={yScale(max)}
                  stroke="currentColor" strokeWidth="0.5" strokeDasharray="2,3"
                  vectorEffect="non-scaling-stroke" />
          </g>

          {/* Linha da média (mais visível) */}
          <line
            x1={padL} x2={w - padR}
            y1={avgY} y2={avgY}
            className="text-cyan-500/50"
            stroke="currentColor" strokeWidth="1" strokeDasharray="4,3"
            vectorEffect="non-scaling-stroke"
          />

          {/* Área + curva */}
          <g className="text-cyan-600 dark:text-cyan-400">
            <path d={areaD} fill="url(#tempspark-fill)" />
            <path d={pathD} fill="none" stroke="currentColor" strokeWidth="1.5"
                  strokeLinejoin="round" strokeLinecap="round"
                  vectorEffect="non-scaling-stroke" />
          </g>

          {/* Ponto + halo do valor atual (vetorial — circle não distorce mas
              tem que usar non-scaling pra raio ser consistente) */}
          <circle cx={lastX} cy={lastY} r="3.5"
                  className="fill-cyan-600 dark:fill-cyan-400"
                  vectorEffect="non-scaling-stroke" />
          <circle cx={lastX} cy={lastY} r="6"
                  className="fill-cyan-600/20 dark:fill-cyan-400/20"
                  vectorEffect="non-scaling-stroke" />
        </svg>

        {/* OVERLAYS HTML — usam % do container, não esticam */}
        {/* Label Y máximo (canto superior esquerdo) */}
        <span
          className="absolute text-[10px] tabular-nums text-slate-500 dark:text-slate-400 pointer-events-none"
          style={{
            left: pctX(padL - 4),
            top: pctY(yScale(max)),
            transform: "translate(-100%, -50%)",
          }}
        >
          {max.toFixed(1)}°
        </span>

        {/* Label Y mínimo (canto inferior esquerdo) */}
        <span
          className="absolute text-[10px] tabular-nums text-slate-500 dark:text-slate-400 pointer-events-none"
          style={{
            left: pctX(padL - 4),
            top: pctY(yScale(min)),
            transform: "translate(-100%, -50%)",
          }}
        >
          {min.toFixed(1)}°
        </span>

        {/* Data inicial (canto inferior esquerdo, abaixo da curva) */}
        <span
          className="absolute text-[10px] tabular-nums text-slate-500 dark:text-slate-400 pointer-events-none"
          style={{
            left: pctX(padL),
            bottom: 0,
          }}
        >
          {_formatAxisDate(first.time, spanHours)}
        </span>

        {/* Data final (canto inferior direito) */}
        <span
          className="absolute text-[10px] tabular-nums text-slate-500 dark:text-slate-400 pointer-events-none"
          style={{
            right: pctX(padR),
            bottom: 0,
            transform: "translateX(100%)",
          }}
        >
          {_formatAxisDate(last.time, spanHours)}
        </span>

        {/* Badge do valor atual — posicionado próximo ao ponto */}
        <span
          className="absolute text-[12px] font-semibold tabular-nums text-cyan-700 dark:text-cyan-300 pointer-events-none"
          style={{
            left: pctX(lastX + 8),
            top: pctY(lastY),
            transform: `translateY(${badgeAbove ? "-100%" : "0"})`,
          }}
        >
          {last.value.toFixed(1)}°
        </span>
      </div>
    </div>
  );
}
